var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { textureCacheService } from "./TextureCacheService";
import { DefaultLoadingManager, Material, MeshBasicMaterial, Texture, TextureLoader } from "three";
import { MeshRenderable } from "../mesh/MeshRenderable";
import { FacetRenderer, isRenderableType, Renderable, RenderableUpdateTriggerState } from "../renderable/Renderable";
import { peerFileSystemService } from "../../multiplayer/peer";
import { offscreenService } from "../../offscreen";
const IMAGE_NOT_FOUND_FILE = "img/FlaVR_Final_Blue.png";
const LOADING_TEXTURE_FILE = "img/Loading.jpg";
const textureLoader = new TextureLoader(DefaultLoadingManager);
textureLoader.setCrossOrigin("anonymous");
const texureNotFoundImage = new Image();
texureNotFoundImage.src = IMAGE_NOT_FOUND_FILE;
const textureNotFound = new Texture(texureNotFoundImage);
texureNotFoundImage.onload = () => {
    textureNotFound.needsUpdate = true;
};
const loadingTextureImage = new Image();
loadingTextureImage.src = LOADING_TEXTURE_FILE;
const loadingTexture = new Texture(loadingTextureImage);
loadingTextureImage.onload = () => {
    loadingTexture.needsUpdate = true;
};
export class ImageToTextureRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.material = new MeshBasicMaterial({ map: textureNotFound });
        this.textureVersion = 0;
        this.currentSrcIsRetained = false;
    }
    destroy() {
        this.material.dispose();
    }
    setVisible(visible) {
        this.material.visible = visible;
        this.material.needsUpdate = true;
    }
    onInit(facet) {
        this.facet = facet;
        this.applyMaterialToRenderable();
        this.facet = undefined;
    }
    updateFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            const needsUpdate = !this.material.map || !this.facet || facet.url !== this.facet.url;
            if (needsUpdate) {
                let texture;
                try {
                    if (this.material.map && this.facet && this.facet.url !== facet.url) {
                        if (this.material.map !== textureNotFound) {
                            if (textureCacheService.has(this.facet.url, this.textureVersion)) {
                                textureCacheService.release(this.facet.url, this.textureVersion);
                                this.currentSrcIsRetained = false;
                            }
                        }
                    }
                    this.material.map = loadingTexture;
                    this.material.needsUpdate = true;
                    if (textureCacheService.has(facet.url, this.textureVersion)) {
                        if (this.currentSrcIsRetained) {
                            textureCacheService.get(facet.url, this.textureVersion);
                        }
                        else {
                            textureCacheService.retain(facet.url, this.textureVersion);
                        }
                    }
                    else {
                        const savedUrl = this.facet ? this.facet.url : undefined;
                        let url = facet.url;
                        if (url === "error") {
                            texture = textureNotFound;
                        }
                        else {
                            const fileId = peerFileSystemService.checkShareUri(url);
                            if (fileId !== undefined) {
                                try {
                                    const peerFile = peerFileSystemService.subscribePeerRemoteFile(fileId);
                                    yield peerFile.onLoadedPromise();
                                    peerFile.onUnloaded.add(() => {
                                        if (this.savedMaterial) {
                                            this.applyMaterialToRenderable(this.savedMaterial);
                                            this.savedMaterial = undefined;
                                        }
                                        else {
                                            this.material.map = textureNotFound;
                                            this.material.needsUpdate = true;
                                        }
                                    });
                                    // ToDo: getOrCreateUrl doesn't has local blob link generated
                                    const newUrl = yield peerFile.createUrl();
                                    if (newUrl === undefined)
                                        return;
                                    url = newUrl;
                                }
                                catch (_a) {
                                    this.material.map = textureNotFound;
                                    this.material.needsUpdate = true;
                                    return;
                                }
                            }
                            const offscreenId = offscreenService.checkUri(url);
                            if (offscreenId !== undefined) {
                                texture = offscreenService.getCanvasTexture(offscreenId);
                                if (!texture) {
                                    texture = textureNotFound;
                                }
                                this.material.map = texture;
                                this.material.needsUpdate = true;
                                return;
                            }
                            texture = yield this.createImageTexture(url);
                            const oldUrl = this.facet ? this.facet.url : undefined;
                            if (oldUrl !== savedUrl && textureCacheService.has(facet.url, this.textureVersion)) {
                                textureCacheService.release(facet.url, this.textureVersion);
                                return;
                            }
                            if (texture) {
                                textureCacheService.set(facet.url, this.textureVersion, texture);
                                this.currentSrcIsRetained = true;
                            }
                            else {
                                texture = textureNotFound;
                                this.currentSrcIsRetained = false;
                            }
                        }
                    }
                }
                catch (error) {
                    console.log("Error loading image", facet.url, error);
                    texture = textureNotFound;
                    this.currentSrcIsRetained = false;
                }
                if (texture) {
                    this.material.map = texture;
                    this.material.needsUpdate = true;
                }
            }
        });
    }
    updateTransform() { }
    onOtherRenderableChanged(state, renderable) {
        switch (state) {
            case RenderableUpdateTriggerState.Changed:
                if (renderable instanceof Renderable) {
                    this.applyMaterialToRenderable();
                }
                break;
            case RenderableUpdateTriggerState.Destroyed:
                break;
        }
    }
    applyMaterialToRenderable(material) {
        const renderable = this.owner.renderable;
        if (!renderable) {
            return;
        }
        if (isRenderableType(MeshRenderable, renderable)) {
            this.applyMaterialToMesh(renderable, material !== null && material !== void 0 ? material : this.material);
        }
    }
    applyMaterialToMesh(renderabel, material) {
        if (!this.facet) {
            return;
        }
        if (!renderabel.meshInstance.isLoaded) {
            return;
        }
        renderabel.meshInstance.traverseMaterial(Material, (_, mesh) => {
            if (!this.savedMaterial && mesh.material !== this.material) {
                this.savedMaterial = mesh.material;
            }
            mesh.material = material;
        });
    }
    createImageTexture(url) {
        return __awaiter(this, void 0, void 0, function* () {
            let texture;
            try {
                texture = (yield textureLoader.loadAsync(url));
            }
            catch (error) {
                console.error(`'${url}' could not be fetched (Error code: ${error.status}; Response: ${error.statusText})`);
            }
            return texture;
        });
    }
}
